import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@dukene/shared';
import {
  ArrowLeft,
  Mail,
  Calendar,
  CheckCircle,
  XCircle,
  AlertCircle,
  Shield,
  ShoppingBag,
  UserCircle,
  DollarSign,
  Package,
  ShoppingCart,
  MapPin,
  Zap,
} from 'lucide-react';
import { format } from 'date-fns';

type UserRole = 'CUSTOMER' | 'MERCHANT' | 'OWNER' | 'ADMIN';
type UserStatus = 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';
type BillingPlan = 'BASIC' | 'PRO' | 'MAX' | 'ENTERPRISE';

interface Store {
  id: string;
  name: string;
  subdomain: string;
  status: string;
  createdAt: string;
}

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  status: UserStatus;
  plan: BillingPlan;
  emailVerified: boolean;
  createdAt: string;
  updatedAt: string;
  ownedStores?: Store[];
  memberships?: any[];
}

interface UserStats {
  totalStores?: number;
  totalProducts?: number;
  totalOrders?: number;
  totalRevenue?: number;
  totalSpent?: number;
  totalAddresses?: number;
}

const statusIcons = {
  ACTIVE: CheckCircle,
  INACTIVE: AlertCircle,
  SUSPENDED: XCircle,
};

const roleIcons = {
  ADMIN: Shield,
  MERCHANT: ShoppingBag,
  OWNER: ShoppingBag,
  CUSTOMER: UserCircle,
};

export default function UserDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Fetch user details
  const { data: user, isLoading, error } = useQuery<User>({
    queryKey: ['user', id],
    queryFn: async () => {
      return apiClient.get<User>(`/users/${id}`);
    },
  });

  // Fetch user stats
  const { data: stats } = useQuery<UserStats>({
    queryKey: ['user-stats', id],
    queryFn: async () => {
      return apiClient.get<UserStats>(`/users/${id}/stats`);
    },
    enabled: !!id,
  });

  // Update user status
  const updateStatusMutation = useMutation({
    mutationFn: async (status: UserStatus) => {
      return apiClient.patch(`/users/${id}/status`, { status });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user', id] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });

  // Update user role
  const updateRoleMutation = useMutation({
    mutationFn: async (role: UserRole) => {
      return apiClient.patch(`/users/${id}/role`, { role });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user', id] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });

  // Update user plan
  const updatePlanMutation = useMutation({
    mutationFn: async (plan: BillingPlan) => {
      return apiClient.patch(`/users/${id}/plan`, { plan });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user', id] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });

  // Reconcile user organisation
  const reconcileMutation = useMutation({
    mutationFn: async () => {
      return apiClient.post(`/users/${id}/reconcile-organisation`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user', id] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
      alert('Organisation reconciled successfully');
    },
    onError: (err: any) => {
      alert(err?.response?.data?.error?.message || 'Failed to reconcile organisation');
    }
  });

  const recalibrateWalletMutation = useMutation({
    mutationFn: async () => {
      return apiClient.post(`/users/${id}/recalibrate-wallet`);
    },
    onSuccess: () => {
      alert('Wallet recalibrated successfully');
    },
    onError: (err: any) => {
      alert(err?.response?.data?.error?.message || 'Failed to recalibrate wallet');
    }
  });

  const provisionStoreMutation = useMutation({
    mutationFn: async () => {
      return apiClient.post(`/users/${id}/provision-default-store`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user', id] });
      queryClient.invalidateQueries({ queryKey: ['user-stats', id] });
      alert('Default store provisioned successfully');
    },
    onError: (err: any) => {
      alert(err?.response?.data?.error?.message || 'Failed to provision default store');
    }
  });

  const enforceLimitsMutation = useMutation({
    mutationFn: async () => {
      return apiClient.post(`/users/${id}/enforce-plan-limits`);
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['user', id] });
      queryClient.invalidateQueries({ queryKey: ['user-stats', id] });
      alert(data?.message || 'Plan limits enforced successfully');
    },
    onError: (err: any) => {
      alert(err?.response?.data?.error?.message || 'Failed to enforce plan limits');
    }
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="flex flex-col items-center gap-4">
          <div className="w-16 h-16 border-4 border-gray-200 border-t-black rounded-full animate-spin"></div>
          <p className="text-gray-600 font-medium">Loading user details...</p>
        </div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="bg-white border border-gray-200 rounded-2xl p-8">
        <div className="text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mb-4 mx-auto">
            <AlertCircle className="w-8 h-8 text-gray-400" />
          </div>
          <h2 className="text-xl font-black text-black mb-2">Failed to Load User</h2>
          <p className="text-gray-500">Please try again later</p>
        </div>
      </div>
    );
  }

  const StatusIcon = (statusIcons as any)[user.status] || AlertCircle;
  const RoleIcon = (roleIcons as any)[user.role] || UserCircle;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/users')}
            className="inline-flex items-center justify-center h-10 px-4 text-sm font-medium rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </button>
          <div>
            <h1 className="text-4xl font-black tracking-tight text-black">
              {user.firstName} {user.lastName}
            </h1>
            <p className="text-gray-500 mt-1">{user.email}</p>
          </div>
        </div>
      </div>

      {/* User Info Card */}
      <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm">
        <div className="flex items-start gap-6">
          <div className="w-20 h-20 bg-black rounded-2xl flex items-center justify-center flex-shrink-0">
            <RoleIcon className="w-10 h-10 text-white" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-4">
              <h2 className="text-2xl font-black text-black">
                {user.firstName} {user.lastName}
              </h2>
              <span className={`inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-bold uppercase tracking-wider ${user.role === 'ADMIN' ? 'bg-black text-white' :
                user.role === 'MERCHANT' || user.role === 'OWNER' ? 'bg-gray-800 text-white' :
                  'bg-gray-100 text-gray-700'
                }`}>
                {user.role}
              </span>
              <span className={`inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-bold uppercase tracking-wider ${user.status === 'ACTIVE' ? 'bg-black text-white' :
                user.status === 'SUSPENDED' ? 'bg-gray-700 text-white' :
                  'bg-gray-100 text-gray-700'
                }`}>
                <StatusIcon className="w-3 h-3" />
                {user.status}
              </span>
              {(user.role === 'MERCHANT' || user.role === 'OWNER') && (
                <span className="inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-bold uppercase tracking-wider bg-black text-white">
                  <Zap className="w-3 h-3" />
                  {user.plan} PLAN
                </span>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-2 text-gray-700">
                <Mail className="w-4 h-4 text-gray-400" />
                <span>{user.email}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-700">
                <Calendar className="w-4 h-4 text-gray-400" />
                <span>Joined {format(new Date(user.createdAt), 'MMMM d, yyyy')}</span>
              </div>
              {user.emailVerified && (
                <div className="flex items-center gap-2 text-black">
                  <CheckCircle className="w-4 h-4" />
                  <span className="font-medium">Email Verified</span>
                </div>
              )}
              <div className="flex items-center gap-2 text-gray-700">
                <Calendar className="w-4 h-4 text-gray-400" />
                <span>Updated {format(new Date(user.updatedAt), 'MMM d, yyyy')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {(user.role === 'MERCHANT' || user.role === 'OWNER') && (
            <>
              <div className="group relative bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-2xl transition-all duration-300">
                <div className="absolute top-6 right-6">
                  <div className="w-12 h-12 bg-black rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                    <ShoppingBag className="w-6 h-6 text-white" />
                  </div>
                </div>
                <div className="mt-16">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Total Stores</p>
                  <p className="text-5xl font-black text-black mt-2">{stats?.totalStores || 0}</p>
                </div>
              </div>

              <div className="group relative bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-2xl transition-all duration-300">
                <div className="absolute top-6 right-6">
                  <div className="w-12 h-12 bg-gray-800 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Package className="w-6 h-6 text-white" />
                  </div>
                </div>
                <div className="mt-16">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Total Products</p>
                  <p className="text-5xl font-black text-black mt-2">{stats?.totalProducts || 0}</p>
                </div>
              </div>

              <div className="group relative bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-2xl transition-all duration-300">
                <div className="absolute top-6 right-6">
                  <div className="w-12 h-12 bg-gray-700 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                    <ShoppingCart className="w-6 h-6 text-white" />
                  </div>
                </div>
                <div className="mt-16">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Total Orders</p>
                  <p className="text-5xl font-black text-black mt-2">{stats?.totalOrders || 0}</p>
                </div>
              </div>

              <div className="group relative bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-2xl transition-all duration-300">
                <div className="absolute top-6 right-6">
                  <div className="w-12 h-12 bg-black rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                    <DollarSign className="w-6 h-6 text-white" />
                  </div>
                </div>
                <div className="mt-16">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Total Revenue</p>
                  <p className="text-5xl font-black text-black mt-2">KES {stats?.totalRevenue?.toLocaleString() || 0}</p>
                </div>
              </div>
            </>
          )}

          {user.role === 'CUSTOMER' && (
            <>
              <div className="group relative bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-2xl transition-all duration-300">
                <div className="absolute top-6 right-6">
                  <div className="w-12 h-12 bg-black rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                    <ShoppingCart className="w-6 h-6 text-white" />
                  </div>
                </div>
                <div className="mt-16">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Total Orders</p>
                  <p className="text-5xl font-black text-black mt-2">{stats?.totalOrders || 0}</p>
                </div>
              </div>

              <div className="group relative bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-2xl transition-all duration-300">
                <div className="absolute top-6 right-6">
                  <div className="w-12 h-12 bg-gray-800 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                    <DollarSign className="w-6 h-6 text-white" />
                  </div>
                </div>
                <div className="mt-16">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Total Spent</p>
                  <p className="text-5xl font-black text-black mt-2">KES {stats?.totalSpent?.toLocaleString() || 0}</p>
                </div>
              </div>

              <div className="group relative bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-2xl transition-all duration-300">
                <div className="absolute top-6 right-6">
                  <div className="w-12 h-12 bg-gray-700 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                </div>
                <div className="mt-16">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Saved Addresses</p>
                  <p className="text-5xl font-black text-black mt-2">{stats?.totalAddresses || 0}</p>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Role Management */}
        <div className="bg-white border border-gray-200 rounded-2xl p-8">
          <h2 className="text-xl font-black text-black mb-6">Role Management</h2>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-bold text-black mb-2 block">User Role</label>
              <select
                value={user.role}
                onChange={(e) => updateRoleMutation.mutate(e.target.value as UserRole)}
                disabled={user.role === 'ADMIN' || updateRoleMutation.isPending}
                className="h-12 w-full rounded-xl border-2 border-gray-200 bg-white px-4 py-2 text-sm font-medium text-black focus:border-black focus:outline-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="CUSTOMER">Customer</option>
                <option value="MERCHANT">Merchant</option>
                <option value="ADMIN">Admin</option>
              </select>
              {user.role === 'ADMIN' && (
                <p className="text-sm text-gray-500 mt-2">
                  Cannot change role of admin users
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Status Management */}
        <div className="bg-white border border-gray-200 rounded-2xl p-8">
          <h2 className="text-xl font-black text-black mb-6">Status Management</h2>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-bold text-black mb-2 block">Account Status</label>
              <select
                value={user.status}
                onChange={(e) => updateStatusMutation.mutate(e.target.value as UserStatus)}
                disabled={user.role === 'ADMIN' || updateStatusMutation.isPending}
                className="h-12 w-full rounded-xl border-2 border-gray-200 bg-white px-4 py-2 text-sm font-medium text-black focus:border-black focus:outline-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
                <option value="SUSPENDED">Suspended</option>
              </select>
              {user.role === 'ADMIN' && (
                <p className="text-sm text-gray-500 mt-2">
                  Cannot change status of admin users
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Plan Management (For Merchants only) */}
        {(user.role === 'MERCHANT' || user.role === 'OWNER') && (
          <div className="bg-white border border-gray-200 rounded-2xl p-8 lg:col-span-2">
            <h2 className="text-xl font-black text-black mb-6">Subscription & Plan</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <label className="text-sm font-bold text-black mb-2 block">Billing Plan</label>
                <select
                  value={user.plan}
                  onChange={(e) => updatePlanMutation.mutate(e.target.value as BillingPlan)}
                  disabled={updatePlanMutation.isPending}
                  className="h-12 w-full rounded-xl border-2 border-gray-200 bg-white px-4 py-2 text-sm font-medium text-black focus:border-black focus:outline-none transition-colors"
                >
                  <option value="BASIC">Basic Plan (1 Store)</option>
                  <option value="PRO">Pro Plan (3 Stores)</option>
                  <option value="MAX">Max Plan (10 Stores)</option>
                  <option value="ENTERPRISE">Enterprise Plan (Unlimited)</option>
                </select>
                <p className="text-[13px] text-gray-500 mt-3 leading-relaxed">
                  Changing the plan will immediately adjust the store limit for this merchant.
                  Existing stores will remain active even if the plan is downgraded.
                </p>
              </div>
              
              <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-black rounded-xl flex items-center justify-center">
                    <Zap className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Active Plan</p>
                    <p className="text-2xl font-black text-black">{user.plan}</p>
                  </div>
                </div>
                {updatePlanMutation.isPending && (
                  <div className="w-6 h-6 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Advanced Reconciliation Actions */}
      {(user.role === 'MERCHANT' || user.role === 'OWNER') && (
        <div className="bg-white border border-gray-200 rounded-2xl p-8 mb-6">
          <h2 className="text-xl font-black text-black mb-6">Advanced Reconciliation</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            
            <div className="border border-gray-200 rounded-xl p-5 hover:border-gray-300 transition-colors">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-black" />
                </div>
                <h3 className="font-bold text-black">Wallet Balance</h3>
              </div>
              <p className="text-sm text-gray-500 mb-4 h-10">Recalculate internal wallet balance from transaction ledger.</p>
              <button
                onClick={() => recalibrateWalletMutation.mutate()}
                disabled={recalibrateWalletMutation.isPending}
                className="w-full h-10 rounded-lg bg-black text-white text-sm font-bold hover:bg-gray-800 focus:ring-2 focus:ring-black focus:ring-offset-2 transition-all disabled:opacity-50"
              >
                {recalibrateWalletMutation.isPending ? 'Recalibrating...' : 'Recalibrate Wallet'}
              </button>
            </div>

            <div className="border border-gray-200 rounded-xl p-5 hover:border-gray-300 transition-colors">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                  <ShoppingBag className="w-5 h-5 text-black" />
                </div>
                <h3 className="font-bold text-black">Default Store</h3>
              </div>
              <p className="text-sm text-gray-500 mb-4 h-10">Scaffold a default store if the merchant has none.</p>
              <button
                onClick={() => provisionStoreMutation.mutate()}
                disabled={provisionStoreMutation.isPending || (user.ownedStores && user.ownedStores.length > 0)}
                className="w-full h-10 rounded-lg bg-black text-white text-sm font-bold hover:bg-gray-800 focus:ring-2 focus:ring-black focus:ring-offset-2 transition-all disabled:opacity-50"
              >
                {provisionStoreMutation.isPending ? 'Provisioning...' : 'Provision Store'}
              </button>
            </div>

            <div className="border border-gray-200 rounded-xl p-5 hover:border-gray-300 transition-colors">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                  <Shield className="w-5 h-5 text-black" />
                </div>
                <h3 className="font-bold text-black">Plan Limits</h3>
              </div>
              <p className="text-sm text-gray-500 mb-4 h-10">Suspend stores exceeding the current billing plan allowance.</p>
              <button
                onClick={() => enforceLimitsMutation.mutate()}
                disabled={enforceLimitsMutation.isPending}
                className="w-full h-10 rounded-lg bg-black text-white text-sm font-bold hover:bg-gray-800 focus:ring-2 focus:ring-black focus:ring-offset-2 transition-all disabled:opacity-50"
              >
                {enforceLimitsMutation.isPending ? 'Enforcing...' : 'Enforce Limits'}
              </button>
            </div>

          </div>
        </div>
      )}

      {/* Reconciliation */}
      {(!user.memberships || !user.memberships.some(m => m.role === 'OWNER')) && (user.role === 'MERCHANT' || user.role === 'OWNER') && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-8 mb-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <AlertCircle className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <h2 className="text-xl font-black text-red-900 mb-2">Needs Reconciliation</h2>
              <p className="text-red-700 text-sm mb-4">
                This user does not currently own any organisation (Account). They may have joined via an invitation to another organisation prior to the current setup. Click below to create a default organisation for them so they can create stores.
              </p>
              <button
                onClick={() => reconcileMutation.mutate()}
                disabled={reconcileMutation.isPending}
                className="inline-flex items-center justify-center h-10 px-6 text-sm font-bold rounded-xl bg-red-600 text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {reconcileMutation.isPending ? 'Creating...' : 'Create Organisation for User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Owned Stores */}
      {user.ownedStores && user.ownedStores.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-2xl p-8">
          <h2 className="text-xl font-black text-black mb-6">Owned Stores</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {user.ownedStores.map((store) => (
              <div key={store.id} className="bg-gray-50 border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-black text-black text-lg">{store.name}</h3>
                    <p className="text-sm text-gray-500 mt-1">{store.subdomain}.dukene.com</p>
                    <span className={`inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-bold uppercase tracking-wider mt-3 ${store.status === 'ACTIVE' ? 'bg-black text-white' : 'bg-gray-100 text-gray-700'
                      }`}>
                      {store.status}
                    </span>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-4">
                  Created {format(new Date(store.createdAt), 'MMM d, yyyy')}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}